import { useCallback, useEffect, useState, type JSX } from 'react';
import { numberOr } from '../../lib/numbers.ts';
import { api, formatBytes, type QueueItem } from '../../api.ts';

/**
 * Work waiting to be read, oldest first.
 *
 * Two outcomes, and they are deliberately not the same button. A mark closes the work; a return
 * sends it back for another go and does not consume an attempt. Returning demands a comment —
 * the server enforces it too — because "returned" with no explanation is the single most
 * demoralising thing an LMS can show a student, and it guarantees the same mistake next time.
 */

function GradeForm({ item, onDone }: { item: QueueItem; onDone: () => void }): JSX.Element {
  const [percent, setPercent] = useState('100');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function act(kind: 'grade' | 'return'): Promise<void> {
    setBusy(true);
    setError('');

    try {
      if (kind === 'grade') {
        await api.authoring.grade(item.id, numberOr(percent, 100) / 100, feedback.trim() || null);
      } else {
        await api.authoring.returnWork(item.id, feedback.trim());
      }
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  const canReturn = feedback.trim().length >= 3;

  return (
    <div className="stack" style={{ marginTop: 14 }}>
      {error ? <p className="error">{error}</p> : null}

      <div className="field">
        <label htmlFor={`fb-${item.id}`}>Комментарий</label>
        <textarea
          id={`fb-${item.id}`}
          className="input"
          rows={3}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Что сделано хорошо и что поправить"
        />
      </div>

      <div className="row">
        <div className="field" style={{ maxWidth: 130, marginBottom: 0 }}>
          <label htmlFor={`gr-${item.id}`}>Оценка, %</label>
          <input
            id={`gr-${item.id}`}
            className="input"
            type="number"
            min={0}
            max={100}
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </div>
        <button type="button" className="btn" disabled={busy} onClick={() => void act('grade')}>
          {busy ? '…' : 'Поставить оценку'}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy || !canReturn}
          onClick={() => void act('return')}
        >
          Вернуть на доработку
        </button>
      </div>
      {!canReturn ? (
        <span className="muted" style={{ fontSize: '0.82rem' }}>
          Вернуть работу можно только с комментарием — иначе ученик не поймёт, что исправлять.
        </span>
      ) : null}
    </div>
  );
}

export function QueuePage(): JSX.Element {
  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [error, setError] = useState('');
  /** Which pile is open: work still waiting, or work already dealt with. */
  const [checked, setChecked] = useState(false);

  const load = useCallback((): void => {
    api.authoring
      .queue(checked)
      .then(setItems)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить очередь'),
      );
  }, [checked]);

  useEffect(load, [load]);

  return (
    <>
      <div className="page-head">
        <h1>Проверка работ</h1>
        <p className="muted">
          {items === null
            ? ''
            : checked
              ? `Проверено: ${items.length}. Сначала недавние.`
              : `Ждут проверки: ${items.length}. Сначала те, что сданы раньше.`}
        </p>
      </div>

      {/*
       * Two piles, not one.
       *
       * Marked work used to disappear the moment it was marked, so a teacher who had just been
       * through twenty pieces of homework could not see what they had said, to whom, or whether
       * anybody had been missed. The only record of their afternoon was in the pupils' cabinets.
       */}
      <div className="tabs">
        <button
          type="button"
          className={checked ? 'tabs__item' : 'tabs__item tabs__item--on'}
          onClick={() => setChecked(false)}
        >
          Ждут проверки
        </button>
        <button
          type="button"
          className={checked ? 'tabs__item tabs__item--on' : 'tabs__item'}
          onClick={() => setChecked(true)}
        >
          Проверенные
        </button>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {items === null && !error ? <p className="muted">Загружаем…</p> : null}
      {items?.length === 0 ? (
        <div className={checked ? 'notice' : 'notice notice--done'}>
          {checked ? 'Проверенных работ пока нет.' : 'Всё проверено — непроверенных работ нет.'}
        </div>
      ) : null}

      {(items ?? []).map((item) => (
        <section key={item.id} className="editcard">
          <header className="editcard__head">
            <strong>{item.student_name}</strong>
            <span className="rowcard__main muted">
              {item.assignment_title} · {item.lesson_title}
            </span>
            {item.is_late ? <span className="badge badge--wait">поздняя сдача</span> : null}
            <span className="muted" style={{ fontSize: '0.82rem' }}>
              попытка №{item.attempt_number}
            </span>
          </header>

          {item.text ? <div className="quoted">{item.text}</div> : null}

          {item.files.length > 0 ? (
            <ul className="filelist filelist--flat" style={{ marginTop: 10 }}>
              {item.files.map((file) => (
                <li key={file.id}>
                  <a href={api.assetUrl(file.asset_id)} className="filelist__name" download>
                    📎 {file.name}
                  </a>
                  <span className="muted">{formatBytes(file.size_bytes)}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {!item.text && item.files.length === 0 ? <p className="muted">Работа пустая.</p> : null}

          {/*
           * A checked piece shows the verdict instead of the form. Offering the buttons again
           * would invite marking the same work twice and quietly overwriting what was said.
           */}
          {checked ? (
            <div className={item.status === 'graded' ? 'notice notice--done' : 'notice notice--accent'}>
              <div>
                <strong>
                  {item.status === 'graded'
                    ? `Оценка: ${Math.round((item.final_fraction ?? 0) * 100)}%`
                    : 'Возвращено на доработку'}
                </strong>
              </div>
              {item.feedback ? <div>{item.feedback}</div> : null}
            </div>
          ) : (
            <GradeForm item={item} onDone={load} />
          )}
        </section>
      ))}
    </>
  );
}
